import { View, StyleSheet, Easing } from "react-native";
import WorkspaceSidebar, {
  COLLAPSED_WIDTH,
  EXPANDED_WIDTH,
} from "../components/chat-companion/WorkspaceSidebar/WorkspaceSidebar";
import WorkspaceThreadsDrawer, {
  WorkspaceThread,
} from "../components/chat-companion/WorkspaceThreadsDrawer/WorkspaceThreadsDrawer";
import {
  useGetAllCompanionChatsForUserQuery,
  useDeleteCompanionChatMutation,
  GetAllCompanionChatsForUserDocument,
} from "../graphql/generated/graphql";
import { formatTimeAgo } from "../utils/date";
import { useWorkspaceThreads } from "../contexts/WorkspaceThreadsContext";
import {
  useWorkspaceLists,
  GENERAL_WORKSPACE_ID,
  TODO_WORKSPACE_ID,
  SETTINGS_WORKSPACE_ID,
} from "../hooks/useWorkspaceLists";
import { useState, useMemo, useRef, useEffect } from "react";
import { Animated } from "react-native";
import { useRoute, RouteProp, useNavigation, NavigationProp, useNavigationState } from "@react-navigation/native";
import { RootStackParamList } from "../navigation/AppNavigator.web";

const DRAWER_WIDTH = 360;

// These are menu items mixed with workspaces, by workspaces we mean chat groups, but for simplicity we call all menu items "workspaces", even though some of them are not really workspaces.
const NAV_WORKSPACES: Record<string, string> = {
  [GENERAL_WORKSPACE_ID]: "Home",
  [TODO_WORKSPACE_ID]: "To do",
  [SETTINGS_WORKSPACE_ID]: "Chat Settings",
};

// Derived reverse mapping: route name -> workspace ID
const ROUTE_TO_WORKSPACE: Record<string, string> = Object.entries(NAV_WORKSPACES).reduce(
  (acc, [workspaceId, routeName]) => {
    acc[routeName] = workspaceId;
    return acc;
  },
  {} as Record<string, string>
);

function CompanionLayout({ children }) {
  const route = useRoute<RouteProp<RootStackParamList, "Home">>();
  const navigation = useNavigation<NavigationProp<RootStackParamList>>();
  const navigationState = useNavigationState((state) => state);
  const { chatId: routeChatId } = route.params || {};

  const currentRouteName = navigationState?.routes[navigationState.index]?.name;
  const { data: chatsData, refetch: refetchChats } = useGetAllCompanionChatsForUserQuery({});
  const [deleteCompanionChat] = useDeleteCompanionChatMutation({
    refetchQueries: [{ query: GetAllCompanionChatsForUserDocument }],
  });
  const {
    openWorkspaceId,
    toggleWorkspaceDrawer,
    setActiveThreadId,
    activeThreadId,
    setOpenWorkspaceId,
    activeWorkspaceId,
    setActiveWorkspaceId,
    isSidebarCollapsed,
  } = useWorkspaceThreads();
  const [searchValue, setSearchValue] = useState("");
  const drawerWidthAnim = useRef(new Animated.Value(0)).current;
  const sidebarValue = useRef(new Animated.Value(isSidebarCollapsed ? COLLAPSED_WIDTH : EXPANDED_WIDTH)).current;
  const hasInitializedRef = useRef(false);
  const generalChats = chatsData?.getAllCompanionChatsForUser?.generalChats ?? [];
  const courseWorkspaces = chatsData?.getAllCompanionChatsForUser?.courseWorkspaces ?? [];
  const supportWorkspaces = chatsData?.getAllCompanionChatsForUser?.supportWorkspaces ?? [];
  const { generalWorkspaces, courseWorkspaceItems, supportWorkspaceItems, todoWorkspaces, settingsWorkspaces } =
    useWorkspaceLists({
      generalChats,
      courseWorkspaces,
      supportWorkspaces,
      activeWorkspaceId,
    });

  const handleWorkspaceThreadsPress = (id: string) => {
    toggleWorkspaceDrawer(id);
  };

  const handleWorkspacePress = (id: string) => {
    setActiveWorkspaceId(id);

    let latestThreadId: string | null = null;
    if (id === GENERAL_WORKSPACE_ID) {
      latestThreadId = generalChats[0]?.id || null;
    } else {
      const workspace = courseWorkspaces.find((w) => w.id === id) || supportWorkspaces.find((w) => w.id === id);
      latestThreadId = workspace?.chats?.[0]?.id || null;
    }

    const routeName = NAV_WORKSPACES[id];

    // If there's a latest thread, navigate to it; otherwise check for special routes
    if (latestThreadId) {
      setActiveThreadId(latestThreadId);
      navigation.navigate("Home", { chatId: latestThreadId });
    } else if (routeName) {
      // Only navigate to special routes if there are no existing threads
      navigation.navigate(routeName as never);
    } else {
      setActiveThreadId(null);
      navigation.navigate("Home", { chatId: undefined });
    }

    // Don't open drawer when clicking workspace
    setOpenWorkspaceId(null);
  };

  const isDrawerOpen = openWorkspaceId !== null;

  useEffect(() => {
    const drawerAnimation = Animated.timing(drawerWidthAnim, {
      toValue: isDrawerOpen ? DRAWER_WIDTH : 0,
      duration: 300,
      easing: Easing.inOut(Easing.ease),
      useNativeDriver: false,
    });

    const leftValue = isSidebarCollapsed ? COLLAPSED_WIDTH : EXPANDED_WIDTH;
    const repositionAnimation = Animated.timing(sidebarValue, {
      toValue: leftValue,
      duration: 300,
      easing: Easing.inOut(Easing.ease),
      useNativeDriver: false,
    });

    Animated.parallel([drawerAnimation, repositionAnimation]).start();
  }, [isDrawerOpen, drawerWidthAnim, isSidebarCollapsed]);

  const activeWorkspaceName = useMemo(() => {
    // Use openWorkspaceId for drawer (which workspace drawer is showing)
    const workspaceIdForDrawer = openWorkspaceId || activeWorkspaceId;
    if (workspaceIdForDrawer === GENERAL_WORKSPACE_ID) {
      return generalWorkspaces[0]?.title || "General";
    }
    const workspace =
      courseWorkspaces.find((w) => w.id === workspaceIdForDrawer) ||
      supportWorkspaces.find((w) => w.id === workspaceIdForDrawer);
    return workspace?.name || "";
  }, [openWorkspaceId, activeWorkspaceId, generalWorkspaces, courseWorkspaces, supportWorkspaces]);

  const threads: WorkspaceThread[] = useMemo(() => {
    // Use openWorkspaceId for drawer (which workspace drawer is showing)
    const workspaceIdForDrawer = openWorkspaceId || activeWorkspaceId;
    if (!workspaceIdForDrawer) return [];

    // If general workspace is open, show general chats
    if (workspaceIdForDrawer === GENERAL_WORKSPACE_ID) {
      return generalChats.map((chat) => ({
        id: chat.id,
        title: chat.name || "Untitled Thread",
        metaLabel: chat.last_modified ? formatTimeAgo(chat.last_modified) : "",
        type: "general" as const,
        isActive: activeThreadId === chat.id,
      }));
    }

    // Otherwise, find the workspace and show its chats
    const workspace =
      courseWorkspaces.find((w) => w.id === workspaceIdForDrawer) ||
      supportWorkspaces.find((w) => w.id === workspaceIdForDrawer);
    if (!workspace || !workspace.chats) return [];

    return workspace.chats.map((chat) => ({
      id: chat.id,
      title: chat.name || "Untitled Thread",
      metaLabel: chat.last_modified ? formatTimeAgo(chat.last_modified) : "",
      type: "general" as const,
      isActive: activeThreadId === chat.id,
    }));
  }, [openWorkspaceId, activeWorkspaceId, generalChats, courseWorkspaces, supportWorkspaces, activeThreadId]);

  const filteredThreads = useMemo(() => {
    if (!searchValue.trim()) return threads;
    const lowerSearch = searchValue.toLowerCase();
    return threads.filter((thread) => thread.title.toLowerCase().includes(lowerSearch));
  }, [threads, searchValue]);

  const taskThreads: WorkspaceThread[] = [];
  const generalThreads: WorkspaceThread[] = filteredThreads;

  const handleSelectThread = (id: string) => {
    // Set active workspace to the one that the drawer is showing
    const workspaceIdForDrawer = openWorkspaceId || activeWorkspaceId;
    if (workspaceIdForDrawer) {
      setActiveWorkspaceId(workspaceIdForDrawer);
    }
    setActiveThreadId(id);
    setOpenWorkspaceId(null);
    navigation.setParams({ chatId: id });
  };

  const handleCreateThread = () => {
    setOpenWorkspaceId(null);
    setActiveThreadId(null);
    navigation.setParams({ chatId: undefined });
  };

  const handleDeleteThread = async (id: string) => {
    try {
      const wasActiveThread = activeThreadId === id;
      const result = await deleteCompanionChat({ variables: { id } });
      if (result.data?.deleteCompanionChat?.success) {
        const refetchResult = await refetchChats();
        // If the deleted thread was the active one, navigate to the latest thread from active workspace
        if (wasActiveThread) {
          const workspaceId = activeWorkspaceId || GENERAL_WORKSPACE_ID;
          let latestThreadId: string | null = null;
          if (workspaceId === GENERAL_WORKSPACE_ID) {
            // After refetch, get the latest thread from general chats
            const updatedGeneralChats = refetchResult.data?.getAllCompanionChatsForUser?.generalChats ?? [];
            latestThreadId = updatedGeneralChats[0]?.id || null;
          } else {
            const updatedCourseWorkspaces = refetchResult.data?.getAllCompanionChatsForUser?.courseWorkspaces ?? [];
            const updatedSupportWorkspaces = refetchResult.data?.getAllCompanionChatsForUser?.supportWorkspaces ?? [];
            const workspace =
              updatedCourseWorkspaces.find((w) => w.id === workspaceId) ||
              updatedSupportWorkspaces.find((w) => w.id === workspaceId);
            latestThreadId = workspace?.chats?.[0]?.id || null;
          }
          if (latestThreadId) {
            setActiveThreadId(latestThreadId);
            navigation.setParams({ chatId: latestThreadId });
          } else {
            setActiveThreadId(null);
            navigation.setParams({ chatId: undefined });
          }
        }
      }
    } catch (error) {
      console.error("Error deleting companion chat:", error);
    }
  };

  useEffect(() => {
    if (!routeChatId) {
      return;
    }
    setActiveThreadId(routeChatId);

    // Determine which workspace this thread belongs to and set it as active
    const isInGeneralChats = generalChats.some((chat) => chat.id === routeChatId);
    if (isInGeneralChats) {
      setActiveWorkspaceId(GENERAL_WORKSPACE_ID);
    } else {
      const workspace =
        courseWorkspaces.find((w) => w.chats?.some((chat) => chat.id === routeChatId)) ||
        supportWorkspaces.find((w) => w.chats?.some((chat) => chat.id === routeChatId));
      if (workspace) {
        setActiveWorkspaceId(workspace.id);
      }
    }

    void refetchChats();
  }, [
    routeChatId,
    setActiveThreadId,
    setActiveWorkspaceId,
    generalChats,
    courseWorkspaces,
    supportWorkspaces,
    refetchChats,
  ]);

  useEffect(() => {
    if (!hasInitializedRef.current && activeWorkspaceId === null && !routeChatId) {
      hasInitializedRef.current = true;
      setActiveWorkspaceId(GENERAL_WORKSPACE_ID);
    }
  }, [activeWorkspaceId, routeChatId, setActiveWorkspaceId]);

  useEffect(() => {
    // Only sync workspace for dedicated routes (e.g., To do, Chat Settings).
    // Avoid resetting to General when we're on Home with no chat selected.
    if (!currentRouteName || currentRouteName === "Home") {
      return;
    }

    const workspaceId = ROUTE_TO_WORKSPACE[currentRouteName];
    if (workspaceId && activeWorkspaceId !== workspaceId) {
      setActiveWorkspaceId(workspaceId);
    }
  }, [currentRouteName, activeWorkspaceId, setActiveWorkspaceId]);

  return (
    <View style={[styles.container, { flexDirection: "column" }]}>
      <View style={styles.content}>
        <WorkspaceSidebar
          generalWorkspaces={generalWorkspaces}
          courseWorkspaces={courseWorkspaceItems}
          supportWorkspaces={supportWorkspaceItems}
          todoWorkspaces={todoWorkspaces}
          settingsWorkspaces={settingsWorkspaces}
          activeWorkspaceId={activeWorkspaceId || GENERAL_WORKSPACE_ID}
          onWorkspacePress={handleWorkspacePress}
          onWorkspaceThreadsPress={handleWorkspaceThreadsPress}
        />
        <View style={styles.childrenWrapper}>{children}</View>
        <Animated.View style={[styles.drawerWrapper, { width: drawerWidthAnim, left: sidebarValue }]}>
          <WorkspaceThreadsDrawer
            visible={isDrawerOpen}
            workspaceName={activeWorkspaceName}
            onClose={() => setOpenWorkspaceId(null)}
            searchValue={searchValue}
            onChangeSearch={setSearchValue}
            onCreateThread={handleCreateThread}
            taskThreads={taskThreads}
            generalThreads={generalThreads}
            onSelectThread={handleSelectThread}
            onDeleteThread={handleDeleteThread}
          />
        </Animated.View>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: "#fff",
    // @ts-expect-error - works on web
    maxHeight: "100vh",
    overflow: "hidden",
  },
  content: {
    flex: 1,
    flexDirection: "row",
  },
  drawerWrapper: {
    position: "absolute",
    top: 0,
    bottom: 0,
    overflow: "hidden",
    zIndex: 10,
  },
  childrenWrapper: {
    flex: 1,
  },
});

export default CompanionLayout;
